import { AnyAction } from 'redux';
import {
  GlobalActions,
  InitializeShopPayload,
  NavigationRoute,
  RoleDeterminedPayload,
  Settings,
  ShowModal,
  SpeichernStatus,
  VertragslaufzeitChanged,
} from '@ps20-frontend/shop-integration-redux';
import { App, FeatureToggles, Regexps, Rolle, Vorvertrag } from '@ps20-frontend/frontend-models';
import { AVLadeVorgangResponse, Kooperation, Vermittlernummer } from '@ps20/bff-models';
import { NewsItem } from '../../app/servicebar/news-modal/news-modal.interface';
import { ToolbarActions } from '../toolbar.actions';
import { IAppState } from '../toolbarState';

export interface TransientState extends Rolle {
  tarifierungIsInProgress: boolean;
  isBeibehaltenesProduktInWarenkorb: boolean;
  beibehaltungAvailable: boolean;
  vorschlagDruckIsInProgress: boolean;
  tiergruppeTakeOverRunning?: boolean;
  openFaq: boolean;
  modal: {
    modalOpen: boolean;
    textContent: ShowModal['textContent'];
    styleObject: ShowModal['styleObject'];
  };
  featureToggles: FeatureToggles;
  regexps: Regexps;
  accessToken?: string;
  zahlungTooltipEnabled: boolean;
  zahlungTooltipOpen: boolean;
  vnTooltipEnabled: boolean;
  vnTooltipOpen: boolean;
  isSdwSession?: boolean;
  isAnonToken?: boolean;
  treuebonusReset: boolean;
  newsNeusteDate: string;
  newsNeusteRead: boolean;
  avLoaded: boolean;
  newsRead: { [key: string]: boolean };
  vermittlernummer: Vermittlernummer;
  settings?: Settings;
  kooperationen: Kooperation[];
  tariffUpdatesLatestDate: string;
  tariffUpdatesRead: boolean;
  customAngebotsdatum?: string;
  cryptId?: string;
  angebotsBuendelId?: string;
  isAnfragepflichtigeInformationen?: boolean;
  isTransferToAgentPage?: boolean;
  uwReviewBlocksEditing?: boolean;
  shopPricingStale?: boolean;
}

export const transientStateReducer = function (
  sectionState: TransientState = null,
  action: AnyAction,
  state: IAppState,
): TransientState {
  switch (action.type) {
    case GlobalActions.ALL_TARIFIERUNGEN_HAVE_ENDED: {
      return { ...sectionState, tarifierungIsInProgress: false };
    }
    case GlobalActions.TARIFIERE_SHOP_STALE: {
      return { ...sectionState, shopPricingStale: true };
    }
    case GlobalActions.TARIFIERE_SHOP_SUCCESS: {
      return sectionState?.shopPricingStale === true
        ? { ...sectionState, shopPricingStale: false }
        : sectionState;
    }
    case GlobalActions.SHOW_MODAL: {
      const actionPayload: ShowModal = action.payload;
      return {
        ...sectionState,
        modal: {
          modalOpen: true,
          textContent: actionPayload.textContent,
          styleObject: actionPayload.styleObject,
        },
      };
    }
    case GlobalActions.CLOSE_MODAL: {
      return {
        ...sectionState,
        modal: { modalOpen: false, textContent: null, styleObject: null },
      };
    }
    case ToolbarActions.OPEN_FAQ: {
      return { ...sectionState, openFaq: action.payload };
    }
    case GlobalActions.FEATURE_TOGGLE_RECEIVED: {
      const actionPayload: FeatureToggles = action.payload;
      return { ...sectionState, featureToggles: actionPayload };
    }
    case GlobalActions.VORSCHLAG_DRUCK_CLICKED: {
      return { ...sectionState, vorschlagDruckIsInProgress: true };
    }
    case GlobalActions.SET_ACCESS_TOKEN: {
      return { ...sectionState, accessToken: action.payload };
    }
    case GlobalActions.VORSCHLAG_DRUCKEN_SUCCESS:
    case GlobalActions.VORSCHLAG_DRUCKEN_ERROR: {
      return { ...sectionState, vorschlagDruckIsInProgress: false };
    }
    case GlobalActions.LOCATION_CHANGED:
    case GlobalActions.NAVIGATION_COMPLETED: {
      const actionPayload: NavigationRoute = action.payload;
      return {
        ...sectionState,
        zahlungTooltipEnabled: actionPayload.appId === App.SHOP_DASHBOARD,
        vnTooltipEnabled: actionPayload.appId !== App.SHOP_CHECKOUT,
      };
    }
    case GlobalActions.TIERGRUPPE_TAKE_OVER_STARTED: {
      return { ...sectionState, tiergruppeTakeOverRunning: true };
    }
    case GlobalActions.TIERGRUPPE_TAKE_OVER_ENDED: {
      return { ...sectionState, tiergruppeTakeOverRunning: false };
    }
    case ToolbarActions.ZAHLUNG_TOOLTIP_TOGGLE: {
      return {
        ...sectionState,
        zahlungTooltipOpen: !sectionState.zahlungTooltipOpen,
      };
    }
    case ToolbarActions.VN_TOOLTIP_TOGGLE: {
      return {
        ...sectionState,
        vnTooltipOpen: !sectionState.vnTooltipOpen,
      };
    }
    case GlobalActions.SHOP_INIT: {
      const actionPayload: InitializeShopPayload = action.payload;
      return {
        ...sectionState,
        isSdwSession: ![null, undefined].includes(actionPayload.sdw?.partyId),
        customAngebotsdatum: actionPayload.customAngebotsdatum,
        cryptId: actionPayload.av.cryptId,
      };
    }
    case GlobalActions.AUTHENTICATION_SUCCESSFUL: {
      const isAnonTokenPl = action.payload.isAnonToken;
      return {
        ...sectionState,
        isAnonToken: isAnonTokenPl,
      };
    }
    case GlobalActions.BEIBEHALTENE_ANGEBOTE_IN_WARENKORB_PROVIDED: {
      return {
        ...sectionState,
        isBeibehaltenesProduktInWarenkorb: action.payload,
      };
    }
    case GlobalActions.UPDATE_VORVERTRAEGE: {
      const standardVertraege: Array<Vorvertrag> = action.payload.standardVorvertraege;
      const isBeibehaltenInStandardVertrag = standardVertraege.find(value => value.beibehaltung);
      return {
        ...sectionState,
        beibehaltungAvailable: isBeibehaltenInStandardVertrag !== undefined,
      };
    }
    case GlobalActions.ROLE_DETERMINED: {
      const actionPayload: RoleDeterminedPayload = action.payload;
      return {
        ...sectionState,
        role: actionPayload.role,
      };
    }
    case GlobalActions.VERTRAGSLAUFZEIT_CHANGED: {
      const changedLaufzeitEvent: VertragslaufzeitChanged = action.payload;
      const treuebonusReset = state.treuebonus.some(
        treuebonus =>
          treuebonus.appId === changedLaufzeitEvent.appId &&
          changedLaufzeitEvent.vertragslaufzeit === 1 &&
          treuebonus.treuebonus_aktuell > 0,
      );
      return { ...sectionState, treuebonusReset };
    }
    case ToolbarActions.TREUEBONUS_RESET_CHANGED: {
      return { ...sectionState, treuebonusReset: action.payload };
    }
    case GlobalActions.REGEXPS_RECEIVED: {
      const actionPayload: Regexps = action.payload;
      return { ...sectionState, regexps: actionPayload };
    }
    case ToolbarActions.UPDATE_NEWS_NEUSTE_DATE: {
      const newsDate: string = action.payload;
      const newsDateFromLocalStorage: string = localStorage.getItem('newsDate');
      if (!newsDateFromLocalStorage) {
        localStorage.setItem('newsDate', newsDate);
      }
      return {
        ...sectionState,
        newsNeusteDate: newsDate,
        newsNeusteRead: newsDate === newsDateFromLocalStorage,
      };
    }
    case ToolbarActions.UPDATE_NEWS_NEUSTE_READ: {
      localStorage.setItem('newsDate', sectionState.newsNeusteDate);
      return { ...sectionState, newsNeusteRead: true };
    }
    case GlobalActions.LOAD_AV_REQUEST_SUCCESSFUL: {
      const payload: AVLadeVorgangResponse = action.payload;
      return { ...sectionState, avLoaded: true, angebotsBuendelId: payload.angebotsBuendelId };
    }
    case ToolbarActions.INIT_MANDATORY_NEWS: {
      const newsItems = action.payload as NewsItem[];
      const newsRead = localStorage.getItem('newsRead')
        ? JSON.parse(localStorage.getItem('newsRead'))
        : {};
      newsItems.forEach(news => {
        if (news.mandatoryToRead) {
          const id = news.id;
          if (!id) {
            throw new Error('Mandatory news must have ID');
          }
          if (newsRead[id] == null) {
            newsRead[id] = false;
          }
        }
      });
      localStorage.setItem('newsRead', JSON.stringify(newsRead));
      return {
        ...sectionState,
        newsRead,
      };
    }
    case ToolbarActions.NEWS_ITEM_CLICKED: {
      const newsRead = { ...sectionState.newsRead };
      if (action.payload.mandatoryToRead && !newsRead[action.payload.id]) {
        newsRead[action.payload.id] = true;
        localStorage.setItem('newsRead', JSON.stringify(newsRead));
      }
      return {
        ...sectionState,
        newsRead,
      };
    }
    case GlobalActions.EINSTIEG_REQUEST_SUCCESSFUL: {
      const vermittlernummer = action.payload.vertreter.defaultVermittlernummer;
      return { ...sectionState, vermittlernummer: vermittlernummer };
    }
    case GlobalActions.VERMITTLER_NUMMER_CHANGED: {
      const vermittlernummer = action.payload.vermittlernummer;
      return { ...sectionState, vermittlernummer };
    }
    case GlobalActions.SETTINGS_RECEIVED:
      return {
        ...sectionState,
        settings: action.payload,
      };
    case GlobalActions.KOOPERATIONEN_LIST_RECEIVED: {
      return {
        ...sectionState,
        kooperationen: action.payload,
      };
    }
    case ToolbarActions.UPDATE_TARIFF_UPDATE_LATEST_DATE: {
      const tariffsUpdateDate: string = action.payload;
      const tariffsUpdateDateLocalStorage: string = localStorage.getItem('tariffUpdateDate');
      if (!tariffsUpdateDateLocalStorage) {
        localStorage.setItem('tariffUpdateDate', tariffsUpdateDate);
      }
      return {
        ...sectionState,
        tariffUpdatesLatestDate: tariffsUpdateDate,
        tariffUpdatesRead: tariffsUpdateDate === tariffsUpdateDateLocalStorage,
      };
    }
    case ToolbarActions.UPDATE_TARIFF_UPDATE_LAST_READ: {
      localStorage.setItem('tariffUpdateDate', sectionState.tariffUpdatesLatestDate);
      return { ...sectionState, tariffUpdatesRead: true };
    }
    case GlobalActions.SAVE_STATUS_RECEIVED: {
      const speichern: SpeichernStatus = action.payload;
      return {
        ...sectionState,
        cryptId: speichern.cryptId,
        angebotsBuendelId: speichern.angebotsBuendelId,
      };
    }
    default:
      return sectionState;
  }
};
